import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Plus, RefreshCw } from "lucide-react";
import { v4 } from "uuid";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useRltOrderPutMutation } from "@/services/backend/rlt/orders/api/order-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { BranchSelectFrom } from "@/services/backend/company/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

interface Props {
  processId: string;
  onCreated: (orderId: string) => void;
  trigger?: ReactNode;
}

export function CreateOrderButton({ processId, onCreated, trigger }: Props) {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(v4());
  const [create, { error, isLoading, reset }] = useRltOrderPutMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    !process || isLoading || branchId === "" || serviceObjectId === "";

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
      setId(v4());
    }
  }, [error, reset, toast]);

  const doCreate = () => {
    if (disabled || !process?.customerId) {
      return;
    }

    create({
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId,
      serviceObjectId,
      offerId: null,
      date: null,
      channel: null,
      customerReference: null,
      internalComment: null,
    })
      .unwrap()
      .then(() => {
        setOpen(false);
        onCreated(id);
        reset();
        setId(v4());
      });
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        {isLoading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-5 w-5" />
        )}
        <span>{t("Auftragseingang")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            {isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            <span>{t("Auftragseingang")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Auftragseingang erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
